import React, { useEffect, useRef, useState } from "react";
import { Animated, Easing, View, Text, StyleSheet, Pressable, Platform } from "react-native";
import Svg, { Path } from "react-native-svg";
import { useNavigation } from "@react-navigation/native";
import { useTheme } from "../../../context/ThemeContext";
import { useMobile } from "../../../hooks/useMobile";
import routes from "../../../navigation/routes";
import { LiveDot, Reveal, WebClass, FONT_BODY, FONT_DISPLAY, FONT_MONO } from "./_shared";

// =============================================================================
// NtVoxcraftHero — centered text band over three phone mockups (incoming call,
// in-call, live ops). The phone visuals mirror the NTConnect mobile app so
// they read as real screenshots; the surrounding marketing surface keeps its
// brand2 blue. Live tickers drive Active calls, Registrations, and MOS.
// =============================================================================

// =============================================================================
// Profile-ring color stops + helper — ported verbatim from the ntvoiplib
// NtProfileImage component so the spinning gradient ring around the avatar
// matches the real app. Implemented as N discrete arc segments (not a CSS
// conic-gradient declaration), so it stays consistent with the "no gradients"
// styling rule while reproducing the same visual.
// =============================================================================
const RING_STOPS = [
  [88, 101, 242],   // #5865F2 blurple
  [235, 69, 158],   // #eb459e pink
  [240, 178, 50],   // #f0b232 amber
  [35, 165, 89],    // #23a559 green
];
function ringColorAt(t) {
  const n = RING_STOPS.length;
  const s = (((t % 1) + 1) % 1) * n;
  const i = Math.floor(s) % n;
  const next = (i + 1) % n;
  const f = s - Math.floor(s);
  return [
    Math.round(RING_STOPS[i][0] + (RING_STOPS[next][0] - RING_STOPS[i][0]) * f),
    Math.round(RING_STOPS[i][1] + (RING_STOPS[next][1] - RING_STOPS[i][1]) * f),
    Math.round(RING_STOPS[i][2] + (RING_STOPS[next][2] - RING_STOPS[i][2]) * f),
  ];
}

// 32 segments is plenty for a 116px ring — fewer arcs than the app's PNG
// generator since SVG strokes anti-alias for free on web.
function RotatingRing({ size, ringWidth = 3, duration = 4000 }) {
  const rotation = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    const anim = Animated.loop(
      Animated.timing(rotation, {
        toValue: 1, duration, easing: Easing.linear,
        // useNativeDriver=false on web so RN-Web translates the transform to CSS.
        useNativeDriver: Platform.OS !== "web",
      }),
    );
    anim.start();
    return () => anim.stop();
  }, [rotation, duration]);

  const spin = rotation.interpolate({ inputRange: [0, 1], outputRange: ["0deg", "360deg"] });
  const cx = size / 2, cy = size / 2;
  const r = (size - ringWidth) / 2;
  const segments = 32;

  const arcs = [];
  for (let i = 0; i < segments; i++) {
    const t0 = i / segments;
    const t1 = (i + 1) / segments;
    const a0 = t0 * Math.PI * 2 - Math.PI / 2;
    const a1 = t1 * Math.PI * 2 - Math.PI / 2;
    const x0 = cx + r * Math.cos(a0);
    const y0 = cy + r * Math.sin(a0);
    const x1 = cx + r * Math.cos(a1);
    const y1 = cy + r * Math.sin(a1);
    const [rc, gc, bc] = ringColorAt((t0 + t1) / 2);
    arcs.push(
      <Path
        key={i}
        d={`M ${x0.toFixed(2)} ${y0.toFixed(2)} A ${r} ${r} 0 0 1 ${x1.toFixed(2)} ${y1.toFixed(2)}`}
        stroke={`rgb(${rc},${gc},${bc})`}
        strokeWidth={ringWidth}
        strokeLinecap="round"
        fill="none"
      />,
    );
  }

  return (
    <Animated.View style={{ position: "absolute", top: 0, left: 0, width: size, height: size, transform: [{ rotate: spin }] }}>
      <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {arcs}
      </Svg>
    </Animated.View>
  );
}

// NTConnect mobile-app palette (used only inside the phone frames).
const APP = {
  bg:        "#040406",
  surface:   "#0c0c0e",
  surface2:  "#15151a",
  border:    "#1f1f24",
  text:      "#e8e8e8",
  text2:     "#9ea0a8",
  text3:     "#6c6f78",
  primary:   "#006AFF",
  success:   "#34C759",
  danger:    "#FF3B30",
};

export default function NtVoxcraftHero() {
  const { theme } = useTheme();
  const nav = useNavigation();
  const isMobile = useMobile();

  // Live tickers — mirror the rhythm of the original hero tiles.
  const [active, setActive] = useState(14);
  const [regs,   setRegs]   = useState(2847);
  const [mos,    setMos]    = useState(4.3);
  useEffect(() => {
    const a = setInterval(() => {
      setActive((n) => Math.max(10, Math.min(22, n + Math.floor(Math.random() * 3) - 1)));
    }, 1800);
    const r = setInterval(() => setRegs((n) => n + Math.floor(Math.random() * 3)), 2400);
    const m = setInterval(() => setMos(() => 4.3 + (Math.random() * 0.2 - 0.1)), 2200);
    return () => { clearInterval(a); clearInterval(r); clearInterval(m); };
  }, []);

  return (
    <View style={[styles.outer, { paddingVertical: isMobile ? 40 : 72 }]}>
      <Reveal style={styles.shell}>
        {/* ---------- Text band (centered, quo-style) ---------- */}
        <View style={[styles.textBand, isMobile && styles.textBandMobile]}>
          <View style={[styles.eyebrow, { borderColor: theme.pillBorder, backgroundColor: theme.pillBg }]}>
            <LiveDot style={{ marginRight: 8 }} />
            <Text style={[styles.eyebrowText, { color: theme.brand2 }]}>MULTI-TENANT · 99.99% SLA</Text>
          </View>
          <Text style={[styles.h1, { color: theme.text }, isMobile && styles.h1Mobile]}>One platform.</Text>
          <Text style={[styles.h1Soft, { color: theme.textDim }, isMobile && styles.h1Mobile]}>
            Every line, every tenant, every minute.
          </Text>
          <Text style={[styles.lede, { color: theme.textDim }]}>
            Voxcraft carries every call across cruise ships, hotels, and command posts — and your team picks them up from the same app on every device.
          </Text>
          <WebClass className="voxcraft-btn">
            <Pressable
              onPress={() => nav.navigate(routes.learnmore)}
              style={({ pressed }) => [
                styles.btn,
                { borderColor: theme.borderSoft, backgroundColor: theme.surface2 },
                pressed && styles.btnPressed,
              ]}
            >
              <Text style={[styles.btnText, { color: theme.text }]}>Learn more →</Text>
            </Pressable>
          </WebClass>
        </View>

        {/* ---------- Phone trio ---------- */}
        <View style={[styles.trio, isMobile && styles.trioMobile]}>
          {!isMobile && (
            <View style={styles.phoneSlot}>
              <Phone><IncomingScreen /></Phone>
            </View>
          )}
          <View style={[styles.phoneSlot, styles.phoneSlotCenter]}>
            <Phone center><InCallScreen /></Phone>
          </View>
          {!isMobile && (
            <View style={styles.phoneSlot}>
              <Phone><OpsScreen active={active} regs={regs.toLocaleString()} mos={mos.toFixed(1)} /></Phone>
            </View>
          )}
        </View>
      </Reveal>
    </View>
  );
}

// =============================================================================
// Phone — outer titanium frame, side hardware buttons, Dynamic Island, screen.
// =============================================================================
function Phone({ children, center }) {
  return (
    <View style={[styles.phoneFrame, center && styles.phoneFrameCenter]}>
      <View style={styles.phone}>
        <View style={styles.notch}>
          <View style={styles.notchDot} />
          <View style={styles.notchCamera} />
        </View>
        <View style={styles.screen}>
          <View style={styles.statusBar}>
            <Text style={styles.statusTime}>9:41</Text>
            <Text style={styles.statusRight}>5G ●●●</Text>
          </View>
          {children}
        </View>
      </View>
      {/* Side hardware buttons (action + 2 volume left, power right) */}
      <View style={[styles.sideBtn, { left: -3, top: 78,  height: 22 }]} />
      <View style={[styles.sideBtn, { left: -3, top: 130, height: 30 }]} />
      <View style={[styles.sideBtn, { left: -3, top: 180, height: 30 }]} />
      <View style={[styles.sideBtn, { right: -3, top: 168, height: 64 }]} />
    </View>
  );
}

// =============================================================================
// Screen 1 — Incoming call
// =============================================================================
function IncomingScreen() {
  return (
    <View style={incoming.wrap}>
      <Text style={incoming.smallLabel}>Incoming call</Text>
      <View style={incoming.acctRow}>
        <View style={incoming.acctDot} />
        <Text style={incoming.acctText}>VOXCRAFT · NCL OFFICE</Text>
      </View>

      <View style={incoming.avatarOuter}>
        <RotatingRing size={124} ringWidth={3} duration={4000} />
        <View style={incoming.avatar}>
          <Text style={incoming.avatarText}>SK</Text>
        </View>
      </View>
      <Text style={incoming.name}>Sarah Kowalski</Text>
      <Text style={incoming.number}>+1 305 555 0142</Text>
      <Text style={incoming.ringing}>Ringing…</Text>

      <View style={incoming.actions}>
        <View style={incoming.action}>
          <View style={[incoming.actionBtn, { backgroundColor: APP.danger, transform: [{ rotate: "135deg" }] }]}>
            <Svg width={28} height={28} viewBox="0 0 24 24"><Path fill="#fff" d="M5 4h6l1.5 4-2.4 1.2a12 12 0 0 0 5.7 5.7L17 12.5l4 1.5v6a1 1 0 0 1-1 1A17 17 0 0 1 4 5a1 1 0 0 1 1-1z" /></Svg>
          </View>
          <Text style={incoming.actionLabel}>Decline</Text>
        </View>
        <View style={incoming.action}>
          <View style={[incoming.actionBtn, { backgroundColor: APP.success }]}>
            <Svg width={28} height={28} viewBox="0 0 24 24"><Path fill="#fff" d="M5 4h6l1.5 4-2.4 1.2a12 12 0 0 0 5.7 5.7L17 12.5l4 1.5v6a1 1 0 0 1-1 1A17 17 0 0 1 4 5a1 1 0 0 1 1-1z" /></Svg>
          </View>
          <Text style={incoming.actionLabel}>Accept</Text>
        </View>
      </View>
    </View>
  );
}

// =============================================================================
// Screen 2 — In-call (active call with IN/OUT level bars + pill control bar)
// =============================================================================
function InCallScreen() {
  return (
    <View style={incall.wrap}>
      <View style={incall.topbar}>
        <View style={incall.backBtn}><Text style={incall.backText}>‹</Text></View>
        <View style={incall.topCenter}>
          <Text style={incall.topName}>Sarah Kowalski</Text>
          <Text style={incall.topSub}>02:35</Text>
        </View>
        <View style={incall.acctChip}><View style={incall.acctChipDot} /></View>
      </View>

      <View style={incall.avBlock}>
        <View style={incall.avatarOuter}>
          <RotatingRing size={108} ringWidth={3} duration={4000} />
          <View style={incall.avatar}><Text style={incall.avatarText}>SK</Text></View>
        </View>
        <View style={incall.levels}>
          <View style={incall.levelRow}>
            <Text style={incall.levelLbl}>IN</Text>
            <View style={incall.levelBar}>
              <WebClass className="voxcraft-hero-lvl-in" style={[incall.levelFill, { backgroundColor: APP.success, width: "72%" }]} />
            </View>
            <Text style={incall.levelVal}>72%</Text>
          </View>
          <View style={incall.levelRow}>
            <Text style={incall.levelLbl}>OUT</Text>
            <View style={incall.levelBar}>
              <WebClass className="voxcraft-hero-lvl-out" style={[incall.levelFill, { backgroundColor: APP.primary, width: "52%" }]} />
            </View>
            <Text style={incall.levelVal}>52%</Text>
          </View>
        </View>
      </View>

      <View style={incall.controls}>
        <View style={incall.pillbar}>
          {/* Mute (toggled on — inverted) */}
          <View style={[incall.pillItem, incall.pillItemOn]}>
            <Svg width={18} height={18} viewBox="0 0 24 24">
              <Path
                fill="none" stroke={APP.bg} strokeWidth={2}
                strokeLinecap="round" strokeLinejoin="round"
                d="M2 2 L22 22 M9 5 a3 3 0 0 1 6 0 v6 M19 10 v2 a7 7 0 0 1 -13.5 2.5 M12 19 v4"
              />
            </Svg>
          </View>
          <View style={incall.pillSep} />
          {/* Keypad — 3x3 dots rendered as SVG circles so the grid is exact */}
          <View style={incall.pillItem}>
            <Svg width={18} height={18} viewBox="0 0 18 18">
              {[3, 9, 15].map((y) =>
                [3, 9, 15].map((x) => (
                  <Path key={`${x}-${y}`} fill={APP.text} d={`M ${x} ${y} m -1.6 0 a 1.6 1.6 0 1 0 3.2 0 a 1.6 1.6 0 1 0 -3.2 0`} />
                )),
              )}
            </Svg>
          </View>
          <View style={incall.pillSep} />
          {/* Speaker */}
          <View style={incall.pillItem}>
            <Svg width={18} height={18} viewBox="0 0 24 24">
              <Path fill={APP.text} d="M3 9 v6 h4 l5 5 V4 L7 9 Z" />
              <Path
                fill="none" stroke={APP.text} strokeWidth={2} strokeLinecap="round"
                d="M16 8 a5 5 0 0 1 0 8 M19 5 a8 8 0 0 1 0 14"
              />
            </Svg>
          </View>
          <View style={incall.pillSep} />
          {/* Hold (pause icon) — two bars side by side */}
          <View style={incall.pillItem}>
            <View style={incall.holdRow}>
              <View style={incall.holdBar} />
              <View style={[incall.holdBar, { marginLeft: 4 }]} />
            </View>
          </View>
        </View>

        <View style={incall.hangup}>
          <View style={{ transform: [{ rotate: "135deg" }] }}>
            <Svg width={26} height={26} viewBox="0 0 24 24"><Path fill="#fff" d="M5 4h6l1.5 4-2.4 1.2a12 12 0 0 0 5.7 5.7L17 12.5l4 1.5v6a1 1 0 0 1-1 1A17 17 0 0 1 4 5a1 1 0 0 1 1-1z" /></Svg>
          </View>
        </View>
      </View>
    </View>
  );
}

// =============================================================================
// Screen 3 — Live ops dashboard (featured Active-calls tile + 2x2 stats grid)
// =============================================================================
function OpsScreen({ active, regs, mos }) {
  return (
    <View style={ops.wrap}>
      <View style={ops.titleRow}>
        <Text style={ops.title}>Operations</Text>
        <View style={ops.liveChip}>
          <View style={ops.liveDot} />
          <Text style={ops.liveText}>LIVE</Text>
        </View>
      </View>

      {/* Featured tile — Active calls */}
      <View style={ops.featured}>
        <View style={ops.featuredLiveDot} />
        <Text style={ops.tileK}>ACTIVE CALLS</Text>
        <Text style={ops.featuredV}>{active}</Text>
        <Text style={ops.featuredDelta}>↑ +3 vs avg</Text>
        <View style={ops.spark}>
          <Svg width="100%" height="100%" viewBox="0 0 100 22" preserveAspectRatio="none">
            <Path fill="rgba(0,106,255,0.12)" stroke="none" d="M 0,16 L 12,12 L 22,18 L 32,10 L 44,14 L 56,7 L 68,11 L 80,5 L 90,9 L 100,3 L 100,22 L 0,22 Z" />
            <Path fill="none" stroke={APP.primary} strokeWidth={1.6} d="M 0,16 L 12,12 L 22,18 L 32,10 L 44,14 L 56,7 L 68,11 L 80,5 L 90,9 L 100,3" />
          </Svg>
        </View>
      </View>

      {/* 2x2 grid */}
      <View style={ops.grid}>
        <View style={ops.tile}>
          <Text style={ops.tileK}>REGISTRATIONS</Text>
          <Text style={ops.tileV}>{regs}</Text>
          <Text style={ops.tileDeltaUp}>↑ +14 / 5m</Text>
        </View>
        <View style={ops.tile}>
          <Text style={ops.tileK}>TENANTS</Text>
          <Text style={ops.tileV}>38</Text>
          <Text style={ops.tileDeltaUp}>↑ +1 wk</Text>
        </View>
        <View style={ops.tile}>
          <Text style={ops.tileK}>UPTIME</Text>
          <Text style={ops.tileV}>99.99%</Text>
          <Text style={ops.tileDeltaUp}>SLA met</Text>
        </View>
        <View style={ops.tile}>
          <Text style={ops.tileK}>MOS · JITTER</Text>
          <Text style={ops.tileV}>{mos} <Text style={ops.tileVSoft}>· 8ms</Text></Text>
          <View style={ops.miniBars}>
            {[50, 80, 35, 65, 90, 55, 75, 45].map((h, i) => (
              <WebClass key={i} className="voxcraft-hero-bar" style={{ width: 3, borderRadius: 1, backgroundColor: APP.primary, height: (h / 100) * 16, opacity: 0.85, marginRight: i === 7 ? 0 : 2 }} />
            ))}
          </View>
        </View>
      </View>

      <View style={ops.foot}>
        <Text style={ops.footText}>WINDOW · <Text style={ops.footBold}>LAST 24H</Text></Text>
        <Text style={ops.footText}>SLA <Text style={[ops.footBold, { color: APP.success }]}>99.97%</Text> · 7-DAY</Text>
      </View>
    </View>
  );
}

// =============================================================================
// Styles
// =============================================================================
const styles = StyleSheet.create({
  outer: { alignItems: "center", paddingHorizontal: 28 },
  shell: { width: "100%", maxWidth: 1180, alignItems: "center" },

  // ---- Text band ----
  textBand: { maxWidth: 760, alignItems: "center", marginBottom: 56 },
  textBandMobile: { marginBottom: 36 },
  eyebrow: {
    flexDirection: "row", alignItems: "center",
    paddingHorizontal: 11, paddingVertical: 5, borderRadius: 999, borderWidth: 1, marginBottom: 22,
  },
  eyebrowText: { fontFamily: FONT_MONO, fontSize: 11, fontWeight: "700", letterSpacing: 1.6 },
  h1:      { fontFamily: FONT_DISPLAY, fontSize: 50, fontWeight: "700", letterSpacing: -1.4, lineHeight: 54, textAlign: "center" },
  h1Soft:  { fontFamily: FONT_DISPLAY, fontSize: 50, fontWeight: "500", letterSpacing: -1.4, lineHeight: 54, marginBottom: 18, textAlign: "center" },
  h1Mobile:{ fontSize: 36, lineHeight: 40 },
  lede:    { fontFamily: FONT_BODY, fontSize: 17, lineHeight: 27, textAlign: "center", maxWidth: 640, marginBottom: 26 },

  btn:      { paddingHorizontal: 22, paddingVertical: 13, borderRadius: 8, borderWidth: 1, alignItems: "center", justifyContent: "center" },
  btnText:  { fontFamily: FONT_BODY, fontSize: 14, fontWeight: "600" },
  btnPressed: { opacity: 0.85, transform: [{ translateY: -1 }] },

  // ---- Trio layout ----
  trio: { flexDirection: "row", alignItems: "flex-end", justifyContent: "center", width: "100%" },
  trioMobile: { flexDirection: "column", alignItems: "center" },
  phoneSlot: { paddingHorizontal: 14, alignItems: "center" },
  phoneSlotCenter: { zIndex: 2 },

  // ---- Phone frame ----
  phoneFrame: {
    width: 290,
    height: 600,
    transform: [{ scale: 0.88 }],
    transformOrigin: "bottom",
    position: "relative",
  },
  phoneFrameCenter: { transform: [{ scale: 1.0 }] },
  phone: {
    width: 290,
    height: 600,
    backgroundColor: "#0c0e12",
    borderRadius: 58,
    padding: 6,
    ...Platform.select({
      web: {
        boxShadow:
          "0 0 0 2px #1f2228, inset 0 0 0 1px #000, 0 60px 110px rgba(0,0,0,0.7), 0 22px 44px rgba(0,0,0,0.45)",
      },
      default: {
        shadowColor: "#000", shadowOpacity: 0.6, shadowRadius: 40, shadowOffset: { width: 0, height: 30 },
      },
    }),
  },

  sideBtn: {
    position: "absolute",
    width: 4,
    backgroundColor: "#2a2c31",
    borderRadius: 2,
  },

  notch: {
    position: "absolute",
    top: 18, left: "50%",
    marginLeft: -55,
    width: 110, height: 32,
    backgroundColor: "#000",
    borderRadius: 18,
    zIndex: 5,
  },
  notchDot:    { position: "absolute", left: 16, top: 13, width: 6, height: 6, backgroundColor: "#14161a", borderRadius: 3 },
  notchCamera: { position: "absolute", right: 12, top: 9, width: 14, height: 14, backgroundColor: "#14161a", borderRadius: 7 },

  screen: {
    flex: 1,
    backgroundColor: APP.bg,
    borderRadius: 52,
    overflow: "hidden",
  },

  statusBar: {
    flexDirection: "row", justifyContent: "space-between", alignItems: "center",
    paddingHorizontal: 32, paddingTop: 16, paddingBottom: 8,
  },
  statusTime:  { fontFamily: FONT_BODY, fontSize: 13, fontWeight: "600", color: APP.text },
  statusRight: { fontFamily: FONT_BODY, fontSize: 11, color: APP.text2 },
});

// ---- Incoming screen styles ----
const incoming = StyleSheet.create({
  wrap: { flex: 1, paddingHorizontal: 24, paddingTop: 18, paddingBottom: 28, alignItems: "center" },
  smallLabel: { fontFamily: FONT_BODY, fontSize: 13, color: APP.text2, fontWeight: "500", marginBottom: 4 },
  acctRow: { flexDirection: "row", alignItems: "center", marginBottom: 34 },
  acctDot: { width: 7, height: 7, borderRadius: 4, backgroundColor: APP.success, marginRight: 7 },
  acctText: { fontFamily: FONT_BODY, fontSize: 11, color: APP.text2, fontWeight: "600", letterSpacing: 1.1 },
  avatarOuter: { width: 124, height: 124, marginBottom: 18 },
  avatar: {
    position: "absolute", top: 4, left: 4,
    width: 116, height: 116, borderRadius: 58,
    backgroundColor: APP.surface2,
    alignItems: "center", justifyContent: "center",
  },
  avatarText: { fontFamily: FONT_BODY, fontSize: 38, fontWeight: "600", color: APP.text },
  name:     { fontFamily: FONT_BODY, fontSize: 22, fontWeight: "600", color: APP.text, marginBottom: 6, letterSpacing: -0.3 },
  number:   { fontFamily: FONT_MONO, fontSize: 14, color: APP.text2, marginBottom: 4 },
  ringing:  { fontFamily: FONT_BODY, fontSize: 13, color: APP.text3, marginTop: "auto", marginBottom: 22 },

  actions: { flexDirection: "row", justifyContent: "center", gap: 60 },
  action:  { alignItems: "center" },
  actionBtn:  { width: 68, height: 68, borderRadius: 34, alignItems: "center", justifyContent: "center" },
  actionLabel:{ marginTop: 8, fontFamily: FONT_BODY, fontSize: 12, color: APP.text2, fontWeight: "500" },
});

// ---- In-call screen styles ----
const incall = StyleSheet.create({
  wrap: { flex: 1, paddingHorizontal: 22, paddingTop: 6, paddingBottom: 28 },

  topbar: { flexDirection: "row", alignItems: "center", paddingVertical: 8 },
  backBtn: { width: 32, height: 32, borderRadius: 16, backgroundColor: APP.surface, alignItems: "center", justifyContent: "center" },
  backText: { color: APP.text, fontSize: 18, lineHeight: 18 },
  topCenter: { flex: 1, alignItems: "center" },
  topName: { fontFamily: FONT_BODY, fontSize: 16, fontWeight: "600", color: APP.text },
  topSub:  { fontFamily: FONT_MONO, fontSize: 12, color: APP.text2, marginTop: 2, letterSpacing: 0.6 },
  acctChip: { width: 32, height: 32, borderRadius: 16, backgroundColor: APP.surface, borderWidth: 1, borderColor: APP.border, alignItems: "center", justifyContent: "center" },
  acctChipDot: { width: 7, height: 7, borderRadius: 4, backgroundColor: APP.success },

  avBlock: { alignItems: "center", marginTop: 14 },
  avatarOuter: { width: 108, height: 108, marginBottom: 14 },
  avatar:  {
    position: "absolute", top: 4, left: 4,
    width: 100, height: 100, borderRadius: 50,
    backgroundColor: APP.surface2,
    alignItems: "center", justifyContent: "center",
  },
  avatarText: { fontFamily: FONT_BODY, fontSize: 32, fontWeight: "600", color: APP.text },

  levels: { width: "100%", marginTop: 4 },
  levelRow: { flexDirection: "row", alignItems: "center", marginBottom: 8 },
  levelLbl: { fontFamily: FONT_MONO, fontSize: 9.5, color: APP.text3, letterSpacing: 1.2, width: 30 },
  levelBar: { flex: 1, height: 5, backgroundColor: APP.surface2, borderRadius: 4, marginHorizontal: 10, overflow: "hidden" },
  levelFill:{ height: 5, borderRadius: 4 },
  levelVal: { fontFamily: FONT_MONO, fontSize: 9.5, color: APP.text2, width: 32, textAlign: "right" },

  controls: { marginTop: "auto", alignItems: "center", paddingTop: 22 },
  pillbar:  { flexDirection: "row", alignItems: "center", paddingVertical: 6, paddingHorizontal: 8, borderWidth: 1, borderColor: APP.border, backgroundColor: "rgba(20,20,24,0.7)", borderRadius: 40, marginBottom: 22 },
  pillItem: { width: 38, height: 38, borderRadius: 19, alignItems: "center", justifyContent: "center" },
  pillItemOn: { backgroundColor: APP.text },
  pillSep:  { width: 1, height: 18, backgroundColor: APP.border, marginHorizontal: 6 },
  holdRow: { flexDirection: "row", alignItems: "center" },
  holdBar: { width: 4, height: 14, backgroundColor: APP.text, borderRadius: 2 },

  hangup: { width: 64, height: 64, borderRadius: 32, backgroundColor: APP.danger, alignItems: "center", justifyContent: "center" },
});

// ---- Ops screen styles ----
const ops = StyleSheet.create({
  wrap: { flex: 1, paddingHorizontal: 16, paddingTop: 14, paddingBottom: 20 },

  titleRow: { flexDirection: "row", alignItems: "baseline", justifyContent: "space-between", paddingHorizontal: 4, paddingBottom: 8 },
  title:    { fontFamily: FONT_BODY, fontSize: 22, fontWeight: "700", color: APP.text, letterSpacing: -0.4 },
  liveChip: { flexDirection: "row", alignItems: "center" },
  liveDot:  { width: 7, height: 7, borderRadius: 4, backgroundColor: APP.success, marginRight: 6 },
  liveText: { fontFamily: FONT_MONO, fontSize: 10, color: APP.success, letterSpacing: 1.4, fontWeight: "700" },

  featured: {
    backgroundColor: APP.surface, borderWidth: 1, borderColor: "rgba(0,106,255,0.45)",
    borderRadius: 14, padding: 14, marginBottom: 10, position: "relative",
  },
  featuredLiveDot: { position: "absolute", top: 12, right: 12, width: 8, height: 8, borderRadius: 4, backgroundColor: APP.success },
  featuredV: { fontFamily: FONT_DISPLAY, fontSize: 38, fontWeight: "700", color: APP.primary, letterSpacing: -0.8, lineHeight: 40 },
  featuredDelta: { fontFamily: FONT_MONO, fontSize: 10, color: APP.success, marginTop: 4 },
  spark: { height: 28, width: "100%", marginTop: 8 },

  grid: { flexDirection: "row", flexWrap: "wrap", justifyContent: "space-between" },
  tile: {
    width: "48.5%",
    backgroundColor: APP.surface, borderWidth: 1, borderColor: APP.border,
    borderRadius: 12, padding: 11,
    marginBottom: 8,
  },
  // Add right margin on first column tiles only — done via index nth pattern below
  tileK: { fontFamily: FONT_MONO, fontSize: 9, color: APP.text3, letterSpacing: 1.1, marginBottom: 4 },
  tileV: { fontFamily: FONT_DISPLAY, fontSize: 20, fontWeight: "700", color: APP.text, letterSpacing: -0.4, lineHeight: 22 },
  tileVSoft: { fontSize: 14, color: APP.text2, fontWeight: "500" },
  tileDeltaUp: { fontFamily: FONT_MONO, fontSize: 9, color: APP.success, marginTop: 3 },
  miniBars: { flexDirection: "row", alignItems: "flex-end", height: 16, marginTop: 6 },

  foot: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", paddingHorizontal: 4, paddingTop: 8, marginTop: "auto" },
  footText: { fontFamily: FONT_MONO, fontSize: 9.5, color: APP.text3, letterSpacing: 1 },
  footBold: { color: APP.text, fontWeight: "600" },
});

